import React, { useCallback, useMemo } from "react";

import { Edit, EyeOff, Play, Power, Trash2, Users } from "lucide-react";

import TruncatedTooltip from "../../../Components/TruncatedTooltip";
import { Video } from "../../../Store/Reducers/VideoSlice";
import { getYouTubeThumbnailUrl } from "../../../Utils/Helper/videoUrl";
import ItemActionsMenu from "../ItemActionsMenu";
import "./style.scss";

export interface AdminVideoCardProps {
  video: Video;
  sharedRoleLabels?: string[];
  /** Small label pinned to the thumbnail's top-right corner. */
  tagLabel?: string;
  /** Override publish state (e.g. aggregate across All subroles). */
  isActiveOverride?: boolean;
  /** Override status action label (defaults to Publish / Unpublish). */
  statusActionLabel?: string;
  isOrderEditMode: boolean;
  /** When false, hide the ⋯ menu (e.g. card Edit Order / DnD). Default true. */
  showActionsMenu?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  onPreview: (video: Video) => void;
  onEdit?: (video: Video) => void;
  onDelete: (video: Video) => void;
  deleteTitle?: string;
  onStatusToggle: (video: Video, toActive: boolean) => void;
  onDragStart?: (videoId: string) => void;
  onDragOver?: (event: React.DragEvent, videoId: string) => void;
  onDrop?: (event: React.DragEvent, videoId: string) => void;
  onDragEnd?: () => void;
}

const AdminVideoCard: React.FC<AdminVideoCardProps> = ({
  video,
  sharedRoleLabels = [],
  tagLabel,
  isActiveOverride,
  statusActionLabel,
  isOrderEditMode,
  showActionsMenu = true,
  isDragging = false,
  isDragOver = false,
  onPreview,
  onEdit,
  onDelete,
  deleteTitle = "Delete video",
  onStatusToggle,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const isActive = isActiveOverride ?? video.is_active ?? true;
  const isDraggable = isOrderEditMode && isActive;
  const thumbnailUrl = getYouTubeThumbnailUrl(video.video_url);
  const formattedDate = new Date(video.created_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const showOrderBadge = isActive && (video.video_order ?? 0) > 0;
  const hasRoleLabels = sharedRoleLabels.length > 0;
  const isShared = sharedRoleLabels.length > 1;
  const roleBadgeText =
    sharedRoleLabels.length <= 3
      ? sharedRoleLabels.join(" · ")
      : `${sharedRoleLabels.slice(0, 2).join(" · ")} +${
          sharedRoleLabels.length - 2
        }`;
  const roleTooltip =
    sharedRoleLabels.length > 0
      ? `Assigned to: ${sharedRoleLabels.join(", ")}`
      : "";

  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      if (!isDraggable) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.effectAllowed = "move";
      onDragStart?.(video.video_id);
    },
    [isDraggable, onDragStart, video.video_id]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!isOrderEditMode) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      onDragOver?.(event, video.video_id);
    },
    [isOrderEditMode, onDragOver, video.video_id]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (!isOrderEditMode) return;
      event.preventDefault();
      onDrop?.(event, video.video_id);
    },
    [isOrderEditMode, onDrop, video.video_id]
  );

  const actions = useMemo(
    () => [
      {
        id: "status",
        label: statusActionLabel || (isActive ? "Unpublish" : "Publish"),
        icon: isActive ? EyeOff : Power,
        onClick: () => onStatusToggle(video, !isActive),
      },
      ...(onEdit
        ? [
            {
              id: "edit",
              label: "Edit",
              icon: Edit,
              onClick: () => onEdit(video),
            },
          ]
        : []),
      {
        id: "delete",
        label: deleteTitle,
        icon: Trash2,
        danger: true,
        onClick: () => onDelete(video),
      },
    ],
    [
      deleteTitle,
      isActive,
      onDelete,
      onEdit,
      onStatusToggle,
      statusActionLabel,
      video,
    ]
  );

  return (
    <article
      className={`admin-video-card ${
        isOrderEditMode && isActive ? "admin-video-card--editable" : ""
      } ${isDragging ? "admin-video-card--dragging" : ""} ${
        isDragOver ? "admin-video-card--drag-over" : ""
      }`}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnd={onDragEnd}
    >
      <button
        type="button"
        className="admin-video-card__preview"
        onClick={() => onPreview(video)}
        title="Click to preview video"
      >
        {showOrderBadge && (
          <div
            className="admin-video-card__order-badge-wrap"
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
          >
            <TruncatedTooltip
              tooltip="Video Display Order"
              className="admin-video-card__order-badge"
            >
              {video.video_order}
            </TruncatedTooltip>
          </div>
        )}

        {hasRoleLabels ? (
          <div
            className="admin-video-card__shared-badge-wrap"
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
          >
            <TruncatedTooltip
              tooltip={roleTooltip}
              className="admin-video-card__shared-badge"
            >
              {isShared ? <Users size={12} aria-hidden /> : null}
              {roleBadgeText}
            </TruncatedTooltip>
          </div>
        ) : tagLabel ? (
          <span className="admin-video-card__tag">{tagLabel}</span>
        ) : null}

        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={video.title || "Video thumbnail"}
            className="admin-video-card__thumbnail"
          />
        ) : (
          <div className="admin-video-card__thumbnail-placeholder">
            <Play size={28} className="text-gray-300" />
          </div>
        )}

        <div className="admin-video-card__overlay">
          <TruncatedTooltip
            tooltip={video.title || "Untitled Video"}
            maxLines={2}
            className="admin-video-card__title"
          >
            {video.title || "Untitled Video"}
          </TruncatedTooltip>
          {video.description && (
            <TruncatedTooltip
              tooltip={video.description}
              maxLines={2}
              className="admin-video-card__description"
            >
              {video.description}
            </TruncatedTooltip>
          )}
        </div>
      </button>

      <div className="admin-video-card__footer admin-video-card__footer--menu">
        <div className="admin-video-card__footer-meta">
          <span className="admin-video-card__footer-label">Created At</span>
          <span className="admin-video-card__footer-value">
            {formattedDate}
          </span>
        </div>
        <span
          className={`admin-video-card__status-pill${
            isActive ? "" : " admin-video-card__status-pill--off"
          }`}
        >
          {isActive ? "Published" : "Unpublished"}
        </span>
        {showActionsMenu ? (
          <div
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
          >
            <ItemActionsMenu actions={actions} ariaLabel="Video actions" />
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default AdminVideoCard;
